<template>
  <div ref="root" class="topbar-overflow-menu">
    <button
      ref="triggerBtn"
      type="button"
      class="admin-topbar-icon admin-topbar-overflow-btn dark-filter-control"
      :class="{ 'admin-topbar-overflow-btn--active': open }"
      aria-label="More topbar actions"
      :aria-expanded="open"
      @click="$emit('toggle')"
    >
      <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
      <span v-if="count > 0" class="topbar-overflow-badge">{{ count }}</span>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        class="topbar-overflow-backdrop"
        aria-hidden="true"
        @click="$emit('close')"
      ></div>
      <div
        v-if="open"
        ref="panel"
        class="topbar-overflow-panel"
        :class="{ 'topbar-overflow-panel--dark': isDarkMode }"
        :data-bs-theme="isDarkMode ? 'dark' : null"
        :style="panelStyle"
        role="dialog"
        aria-label="Topbar overflow menu"
      >
        <slot />
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'TopbarOverflowMenu',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    count: {
      type: Number,
      default: 0
    },
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle', 'close'],
  data() {
    return {
      panelStyle: {}
    };
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.updatePanelPosition();
          this.bindPositionListeners();
        });
      } else {
        this.unbindPositionListeners();
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    this.unbindPositionListeners();
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        this.$emit('close');
      }
    },
    bindPositionListeners() {
      window.addEventListener('resize', this.updatePanelPosition);
      window.addEventListener('scroll', this.updatePanelPosition, true);
    },
    unbindPositionListeners() {
      window.removeEventListener('resize', this.updatePanelPosition);
      window.removeEventListener('scroll', this.updatePanelPosition, true);
    },
    updatePanelPosition() {
      const trigger = this.$refs.triggerBtn;
      if (!trigger) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const panelWidth = Math.min(320, window.innerWidth * 0.92);
      const left = Math.max(12, Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - 12));

      this.panelStyle = {
        position: 'fixed',
        top: `${rect.bottom + 8}px`,
        left: `${left}px`,
        width: `${panelWidth}px`,
        zIndex: 1060
      };
    }
  }
};
</script>

<style scoped>
.topbar-overflow-menu {
  position: relative;
  flex: 0 0 auto;
}

.admin-topbar-overflow-btn {
  position: relative;
  width: 38px;
  height: 38px;
  border: 1px solid #f1f1f1;
  border-radius: 8px;
  background: #fff;
  color: #6d788a;
  font-size: 14px;
}

.admin-topbar-overflow-btn--active {
  border-color: #2d64b8;
  color: #2d64b8;
  background: #eef4fc;
}

.topbar-overflow-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 2px;
  background: #2d64b8;
  color: #fff;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
}
</style>

<style>
.topbar-overflow-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  background: transparent;
}

.topbar-overflow-panel {
  position: fixed;
  z-index: 1060;
  min-width: 240px;
  max-height: none;
  overflow: visible;
  padding: 12px 16px 12px 12px;
  border: 1px solid #f1f1f1;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.topbar-overflow-panel--dark {
  border-color: #283548;
  background: #111827;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.topbar-overflow-panel--dark .topbar-overflow-panel-label {
  color: #9aa8bc;
}

.topbar-overflow-panel-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
}

.topbar-overflow-panel-row--inline {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
}

.topbar-overflow-panel-row--utility {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.topbar-overflow-panel-row--utility .topbar-overflow-panel-label {
  margin-bottom: 0;
}

.topbar-overflow-panel-row--utility .admin-topbar-icon,
.topbar-overflow-panel-row--utility .admin-topbar-help-link {
  margin-left: auto;
  margin-right: 4px;
}

.topbar-overflow-panel-label {
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  color: #6d788a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.topbar-overflow-panel-row .topbar-dashboard-select,
.topbar-overflow-panel-row .topbar-dashboard-select--panel {
  flex: 0 0 auto;
  width: auto;
  min-width: 250px;
  max-width: none;
  height: 40px;
  box-sizing: border-box;
  padding: 4px 36px 4px 12px;
}

.topbar-overflow-panel-row--utility .topbar-dashboard-select,
.topbar-overflow-panel-row--utility .topbar-dashboard-select--panel,
.topbar-overflow-panel-row--utility .topbar-select-filter {
  margin-left: auto;
}

.topbar-overflow-panel-row--utility .topbar-select-filter-toggle {
  height: 40px;
  min-height: 40px;
  box-sizing: border-box;
}

.topbar-overflow-panel-row .admin-topbar-search,
.topbar-overflow-panel-row .topbar-context-filter,
.topbar-overflow-panel-row .topbar-filter-group,
.topbar-overflow-panel-row .topbar-utility-icons {
  width: 100%;
}

.topbar-overflow-panel-row .topbar-date-field {
  flex: 1 1 calc(50% - 6px);
  width: auto;
  min-width: 120px;
}

.topbar-overflow-panel-row .admin-topbar-btn {
  flex: 1 1 auto;
}
</style>
